/**
 * The sound cues of a recording (owner ruling D2, 2026-10-02; `PRODUCT.md`
 * §3.1, Record row: "Sound: only cues").
 *
 * The recording is three minutes with eyes closed, so the person cannot see
 * that it has ended. One soft two-note tone says "open your eyes and look"; the
 * screen says what happened. In a paired session (Sprint 1.9.13, ruling R4) a
 * second cue, one shorter note, marks the switch from eyes open to eyes
 * closed: it is the end of block 1 and the only sound at that boundary.
 * The phase machine decides WHEN (a `cue` effect at full length, at the block
 * boundary, or when the headband is lost; never on Stop early); this module
 * only makes the sound.
 *
 * Rules:
 *   - The tone is generated with the Web Audio API. No audio file, no dependency.
 *   - Creating a player creates no audio context. `prime()` does, and it must be
 *     called synchronously inside the Start click: Chrome refuses to start a
 *     context made outside a user gesture (its autoplay rule).
 *   - `play()` sounds only on a primed, open context; otherwise it does nothing.
 *   - `close()` forgets the context, so a later `prime()` makes a fresh one.
 *     Development `StrictMode` (`main.tsx`) mounts, unmounts and mounts the
 *     capture page on the same controller; a player dead after `close()` would
 *     never sound under `npm run dev`.
 *   - NOTHING HERE THROWS. Sound is a courtesy: if it is unavailable (no Web
 *     Audio, a refused autoplay, any exception), the recording, the journal and
 *     the upload go on exactly as before, with no error shown.
 *
 * `calibrations/static/capture/cue.mjs` is the same module for the recorder:
 * keep the tone table identical in both.
 */

/**
 * `end`: the recording (or the second block) is over, open your eyes.
 * `switch`: block 1 (eyes open) is over, close your eyes; block 2 is recording.
 */
export type CueKind = 'end' | 'switch'

export interface CueTone {
  /** Pitch of the sine note. */
  freqHz: number
  /** When the note starts, from the moment `play()` is called. */
  startS: number
  /** How long the note lasts, fade-out included. */
  durS: number
}

/**
 * THE TONE TABLE. `end` is two sine notes, 880 Hz then 660 Hz; `switch` is one
 * 660 Hz note of half a second, so the two cues are told apart with the eyes
 * closed and neither is louder than the other. Identical in `cue.mjs`.
 */
export const CUE_TONES: Readonly<Record<CueKind, readonly CueTone[]>> = {
  end: [
    { freqHz: 880, startS: 0, durS: 0.35 },
    { freqHz: 660, startS: 0.4, durS: 0.6 },
  ],
  switch: [{ freqHz: 660, startS: 0, durS: 0.5 }],
}
/** Peak gain of each note: soft. */
export const CUE_PEAK_GAIN = 0.2
/** Linear fade-in at the start of each note. */
export const CUE_FADE_IN_S = 0.02
/** The exponential fade-out ends here; an exponential ramp cannot reach 0. */
export const CUE_FLOOR_GAIN = 0.0001

/* The small part of the Web Audio API the cue uses, so a test can drive it. */
export interface AudioParamLike {
  setValueAtTime(value: number, time: number): unknown
  linearRampToValueAtTime(value: number, time: number): unknown
  exponentialRampToValueAtTime(value: number, time: number): unknown
}
export interface AudioNodeLike {
  connect(destination: object): unknown
}
export interface OscillatorLike extends AudioNodeLike {
  type: string
  readonly frequency: AudioParamLike
  start(when: number): void
  stop(when: number): void
}
export interface GainLike extends AudioNodeLike {
  readonly gain: AudioParamLike
}
export interface AudioContextLike {
  readonly currentTime: number
  readonly state: string
  readonly destination: object
  resume(): Promise<void>
  close(): Promise<void>
  createOscillator(): OscillatorLike
  createGain(): GainLike
}

export interface CuePlayer {
  /** Create (once) and resume the audio context. Call inside the Start click. */
  prime(): void
  /** Play a cue on the primed context; does nothing without one. */
  play(kind: CueKind): void
  /** Close the context and forget it. */
  close(): void
}

function defaultContext(): AudioContextLike | null {
  if (typeof AudioContext === 'undefined') return null // jsdom, old browsers
  return new AudioContext()
}

/** Swallow a rejected promise: a refused resume or close is not an error here. */
function quiet(p: unknown): void {
  if (p && typeof (p as Promise<unknown>).catch === 'function') (p as Promise<unknown>).catch(() => undefined)
}

function schedule(ctx: AudioContextLike, tone: CueTone): void {
  const t0 = ctx.currentTime + tone.startS
  const t1 = t0 + tone.durS
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(tone.freqHz, t0)
  gain.gain.setValueAtTime(0, t0)
  gain.gain.linearRampToValueAtTime(CUE_PEAK_GAIN, t0 + CUE_FADE_IN_S)
  gain.gain.exponentialRampToValueAtTime(CUE_FLOOR_GAIN, t1)
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.start(t0)
  osc.stop(t1)
}

export function createCuePlayer(makeContext: () => AudioContextLike | null = defaultContext): CuePlayer {
  let ctx: AudioContextLike | null = null
  const open = () => (ctx && ctx.state !== 'closed' ? ctx : null)

  return {
    prime() {
      try {
        if (!open()) ctx = makeContext()
        if (ctx) quiet(ctx.resume())
      } catch {
        // No sound; the recording goes on.
      }
    },
    play(kind) {
      try {
        const c = open()
        if (!c) return
        if (c.state === 'suspended') quiet(c.resume())
        for (const tone of CUE_TONES[kind]) schedule(c, tone)
      } catch {
        // No sound; the recording goes on.
      }
    },
    close() {
      const c = ctx
      ctx = null
      try {
        if (c) quiet(c.close())
      } catch {
        // Already gone.
      }
    },
  }
}
